import * as React from 'react'
import { MotionVariant } from '@ds/motion'
import { dataProps } from '@ds/react-utils'
import { consoleWarn } from '../../../logging'
import { useThemeStyles } from '../../../theming'
import type { DivForwardRef } from '../../../types'
import { DATA_DSUI_MODAL } from '../useModalDetection'
import { ModalBody, ModalBodyProps } from '../ModalBody'
import type { ModalCloseProps } from '../ModalClose'
import { ModalHeader } from '../ModalHeader'
import type { ModalHeight, ModalWidth, ModalRoles } from '../Modal'
import styles from './styles'

const DEPRECATED_Z_INDEX = 800

export interface ModalBaseProps {
  /**
   * The title to present to assistive devices in order to identify the Modal.
   * If not provided, the accessibility title will be set to the Modal.Header title instead.
   * (!) You must provide at least one of either an accessibilityTitle or a `<Modal.Header>`.
   */
  accessibilityTitle?: string
  /**
   * The `children` prop accepts `<Modal.Header>`, `<Modal.Body>`, and/or `<Modal.Footer>` components as well as an element that receives inital focus.
   * (!) You must provide at least one of either an accessibilityTitle or a `<Modal.Header>`.
   */
  children: React.ReactNode
  /**
   * Adds a close button to the Modal.
   * The value should be a Modal.Close component, which takes an 'onClick'
   * prop whose value should be a function to invoke when it is clicked.
   */
  closeButton?: React.ReactElement<ModalCloseProps>
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Disables the modal.
   */
  disabled?: boolean
  /**
   * A React ref to assign to the HTML label element.
   */
  forwardedRef?: DivForwardRef
  /**
   * Height of the modal.
   */
  height?: ModalHeight
  /**
   * Internal style prop to change container's min-height for the ImageModal.
   * @internal @ignore
   */
  hasImage?: boolean
  /**
   * Applies the `role` attribute for assistive technology.
   *
   * The default is `role="dialog"` which is suitable for most modals.
   * Set `role="alertdialog"` if the Modal contains an error message, alert, or warning.
   *
   * See the [Alert Modal configuration](./index.html?path=/docs/components-modal-configurations-developer--alert-modal) for an example.
   */
  role?: ModalRoles
  /**
   * Width of the modal.
   */
  width?: ModalWidth
  /**
   * The z-index to apply to the ModalBase (note that it is displayed using CSS position: fixed).
   */
  zIndex?: number
}

export function ModalBase({
  accessibilityTitle,
  children,
  closeButton,
  'data-qa': dataQa,
  disabled = false,
  forwardedRef,
  hasImage,
  height,
  role = 'dialog',
  width,
  zIndex = DEPRECATED_Z_INDEX,
  ...restProps
}: ModalBaseProps) {
  const sx = useThemeStyles(styles, {
    hasImage,
    height,
    width,
  })

  const modalContainerStyles = [sx.default.wrap, { zIndex }]

  const modalBaseStyles = [
    sx.default.modalBase,
    disabled && sx.disabled.modalBase,
  ]

  const closeButtonStyles = [sx.default.close, disabled && sx.disabled.close]

  const closeButtonNode = closeButton && (
    <div css={closeButtonStyles}>{closeButton}</div>
  )

  let ariaText: string | undefined = accessibilityTitle

  const childrenNode = React.Children.map(children, (child) => {
    let headerText
    if (React.isValidElement(child) && child.type === ModalHeader) {
      headerText = child.props?.title
      ariaText = ariaText || headerText
    }

    if (!React.isValidElement(child) || child.type !== ModalBody) return child

    return React.cloneElement(child as React.ReactElement<ModalBodyProps>, {
      accessibilityText: ariaText,
    })
  })

  if (!ariaText) {
    consoleWarn(
      'You must provide at least one of either an accessibilityTitle or a `<Modal.Header>`.',
    )
  }

  return (
    <MotionVariant
      {...dataProps(restProps)}
      data-qa={dataQa}
      {...{ [DATA_DSUI_MODAL]: 'true' }}
      aria-hidden={disabled ? 'true' : undefined}
      aria-label={ariaText}
      aria-modal="true"
      css={modalContainerStyles}
      key="modal"
      ref={forwardedRef}
      role={role}
      variants={sx.motionVariants.wrap}
    >
      <div data-qa={dataQa && `${dataQa}-content`} css={modalBaseStyles}>
        {closeButtonNode}
        {childrenNode}
      </div>
    </MotionVariant>
  )
}

Object.defineProperty(ModalBase, 'zIndex', {
  get: () => {
    consoleWarn(`
        WARN @ds/ui: This z-index (${DEPRECATED_Z_INDEX}) accessed by Modal.Base.zIndex
        is the default value applied when mounted directly by a consumer of the DsUi
        library. This default value is deprecated and the property will be removed in
        the next major release, if you require a specific z-index (or are expecting a specific
        z-index, like the now deprecated default) you should specify it using the new zIndex
        prop on the Modal.Base component.
      `)
    return DEPRECATED_Z_INDEX
  },
})

ModalBase.displayName = 'Modal.Base'
